const createCanvas = (width: number, height: number) => {
  return new OffscreenCanvas(width, height);
};

const getContext = (width: number, height: number) => {
  const canvas = createCanvas(width * 2, height * 2);
  return {
    context: canvas.getContext('2d', { alpha: true }),
    canvas,
  };
};

type TiledImageProps = {
  src: string;
  height: number;
  width: number;
  padding: number;
  imageHeight: number;
  imageWidth: number;
  rotation: number;
};

const map = new Map();

const getImageBitmap = async ({ src, imageWidth, imageHeight }: TiledImageProps) => {
  const key = `${src}.${imageWidth}.${imageHeight}`;
  if (map.has(key)) {
    return map.get(key);
  }

  const response = await fetch(src, { cache: 'force-cache' });
  const imgBlob = await response.blob();
  const bitmap = await createImageBitmap(imgBlob, { resizeHeight: imageHeight });
  const aspectRatio = bitmap.width / bitmap.height;
  const targetAspectRatio = imageWidth / imageHeight;
  const scale = targetAspectRatio < aspectRatio ? imageHeight / bitmap.height : imageWidth / bitmap.width;
  const res = {
    bitmap,
    height: bitmap.height * scale,
    width: bitmap.width * scale,
  };

  map.set(key, res);
  return res;
};

const tileCache = new Map<string, Blob>();
const MAX_CACHE = 100;
function trimMap() {
  if (tileCache.size <= MAX_CACHE) return tileCache;

  // Get an iterator of all keys
  const keys = tileCache.keys();

  // Loop through keys and remove until size is correct
  while (tileCache.size > MAX_CACHE) {
    tileCache.delete(keys.next().value as string);
  }
  return tileCache;
}

function setTileCache(key: string, blob: Blob) {
  tileCache.set(key, blob);
  trimMap();
}

export const generateTiledImage = async (props: TiledImageProps) => {
  const key = JSON.stringify(props);
  if (tileCache.has(key)) {
    return URL.createObjectURL(tileCache.get(key) as Blob);
  }

  const image = await getImageBitmap(props);
  const { height, width, padding, imageHeight, imageWidth, rotation } = props;
  const size = Math.sqrt(width ** 2 + height ** 2);
  const overdraw = 3;
  const repeatX = Math.ceil(size / imageWidth) * overdraw;
  const repeatY = Math.ceil(size / imageHeight) * overdraw;
  const { context, canvas } = getContext(size, size);
  if (!context) return null;
  context.translate(size, size);
  context.rotate((rotation * Math.PI) / 180);
  const offsetTop = size;
  const offsetLeft = size / 2;
  for (let i = 0; i < repeatX; i++) {
    for (let ii = 0; ii < repeatY; ii++) {
      const tileFactor = i % 2 ? 0.5 : 1;
      const x = -image.width + (i * image.width + padding * i - size);
      const y = -image.height * tileFactor + ii * image.height + padding * ii - size;
      const h = image.height || 100;
      const w = image.width || 100;
      context.drawImage(image.bitmap, x - offsetLeft, y - offsetTop, w, h);
    }
  }

  const blob = await canvas.convertToBlob();
  const url = URL.createObjectURL(blob);
  setTileCache(key, blob);
  return url;
};
